// Deterministic scenario builder (spec §6.3): approved program parameters +
// the rights envelope → a draft proposed-state matrix. Pure function, no LLM —
// the AI only translated free text into params; the numbers here come from the
// rights engine and explicit, listed assumptions. Every generated cell is
// tagged ESTIMATED so the user knows it's a draft to edit, not a calculation.
import { computeRights, gatherInputs, fmt } from './rights-engine';
import { defaultColumns, makeRow, cellKey } from '../state/matrix';
import { STATUS } from '../state/model';

const num = (v) => {
  if (v == null) return null;
  const n = Number(String(v).replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : null;
};

function getParam(params, keyParts, labelParts = []) {
  const hit = (params || []).find((pr) =>
    keyParts.some((k) => String(pr.key || '').includes(k)) ||
    labelParts.some((l) => String(pr.label || '').includes(l)));
  return hit ? hit.value : null;
}

// Fixed service-area allowances per floor (m²) — typical stair core, elevator
// shaft, and landing; the ground floor gets a larger entrance lobby.
const SERVICE = { stairs: 12, elevator: 5, lobby: 8, groundLobby: 20 };

export function buildMatrixFromProgram(project, scenario) {
  const inputs = gatherInputs(project);
  const metrics = computeRights(inputs);
  const allowedTotal = metrics.find((m) => m.key === 'allowedTotal');
  const allowedCoverage = metrics.find((m) => m.key === 'allowedCoverage');
  if (allowedTotal.status !== 'ok') {
    return { error: 'חסרים נתוני זכויות לבניית המטריצה (שטח מגרש / אחוזי בנייה) — השלם בלשונית "זכויות".' };
  }

  const assumptions = [];
  const params = scenario.program?.params || [];

  // Floors: the plan's stories limit, else a default the user must fix.
  let floors = inputs.maxStories;
  if (!floors) {
    floors = 3;
    assumptions.push('מספר קומות לא ידוע — הונחו 3 קומות (יש לעדכן).');
  }
  floors = Math.max(1, Math.min(12, Math.round(floors)));

  // Per-floor envelope: allowed coverage caps the footprint; the total-area
  // budget spread over the floors caps it too. Take the binding one.
  const perFloorBudget = allowedTotal.value / floors;
  const perFloor = allowedCoverage.status === 'ok'
    ? Math.min(allowedCoverage.value, perFloorBudget)
    : perFloorBudget;
  assumptions.push(`מעטפת: ${fmt(allowedTotal.value)} מ"ר מותר ÷ ${floors} קומות → עד ${fmt(perFloor)} מ"ר לקומה${allowedCoverage.status === 'ok' ? ` (תכסית מותרת ${fmt(allowedCoverage.value)} מ"ר)` : ''}.`);

  // Program parameters.
  let unitsPerFloor = num(getParam(params, ['units_per_floor'], ['דירות בקומה']));
  if (!unitsPerFloor || unitsPerFloor < 1) {
    unitsPerFloor = 2;
    assumptions.push('מספר דירות בקומה לא צוין — הונחו 2 דירות בקומה.');
  } else {
    assumptions.push(`${unitsPerFloor} דירות בקומה לפי הפרוגרמה.`);
  }

  const groundMainRaw = getParam(params, ['ground_floor_main'], ['עיקרי בקרקע']);
  const noGroundMain = groundMainRaw != null && (num(groundMainRaw) === 0 || /בלי|ללא|אין|לא/.test(String(groundMainRaw)));
  if (noGroundMain) assumptions.push('קומת הקרקע ללא שטח עיקרי לפי הפרוגרמה — היתרה הוקצתה לחניה ולמבואה מוגדלת.');

  assumptions.push(`שטחי שירות הונחו: מדרגות ${SERVICE.stairs}, מעלית ${SERVICE.elevator}, מבואה ${SERVICE.lobby} (בקרקע ${SERVICE.groundLobby}) מ"ר לקומה — אומדן לעריכה.`);

  const additionPerUnit = num(getParam(params, ['addition_per_existing'], ['תוספת לכל דירה', 'תוספת לדירה']));
  if (additionPerUnit) {
    const exUnits = inputs.existingUnits;
    assumptions.push(`הפרוגרמה כוללת תוספת ${additionPerUnit} מ"ר לדירה קיימת${exUnits ? ` (×${exUnits} דירות = ${fmt(additionPerUnit * exUnits)} מ"ר)` : ''} — תוספות לדירות קיימות לא שולבו במטריצה ודורשות תכנון פרטני.`);
  }

  // Build the matrix: ground + upper floors, default element columns.
  const columns = defaultColumns();
  const col = (key) => columns.find((c) => c.key === key);
  const rows = [makeRow({ level: 0, label: 'קרקע' })];
  for (let i = 1; i < floors; i++) rows.push(makeRow({ level: i, label: `קומה ${i}` }));

  const cells = {};
  const put = (rowId, colId, sqm) => {
    if (sqm > 0) cells[cellKey(rowId, colId)] = {
      sqm, status: STATUS.ESTIMATED, source: 'נוצר מהפרוגרמה', confidence: null, note: null,
    };
  };

  let estimatedUnits = 0;
  rows.forEach((row, idx) => {
    const isGround = idx === 0;
    const lobby = isGround ? SERVICE.groundLobby : SERVICE.lobby;
    const service = SERVICE.stairs + SERVICE.elevator + lobby;
    const residual = Math.max(0, Math.floor(perFloor - service));
    put(row.id, col('stairs').id, SERVICE.stairs);
    put(row.id, col('elevator').id, SERVICE.elevator);
    put(row.id, col('lobby').id, lobby);
    if (isGround && noGroundMain) {
      put(row.id, col('parking').id, residual);
    } else {
      put(row.id, col('apartments').id, residual);
      estimatedUnits += unitsPerFloor;
    }
  });

  const residentialFloors = noGroundMain ? floors - 1 : floors;
  const perUnit = residentialFloors > 0 ? Math.floor((perFloor - SERVICE.stairs - SERVICE.elevator - SERVICE.lobby) / unitsPerFloor) : 0;
  if (estimatedUnits > 0) assumptions.push(`סה"כ ${estimatedUnits} יח"ד משוערות (${unitsPerFloor} × ${residentialFloors} קומות מגורים), כ-${fmt(perUnit)} מ"ר ברוטו לדירה.`);

  return {
    matrix: { columns, rows, cells },
    assumptions,
    estimatedUnits,
  };
}
